import { type ThemeContext } from './themeContext';
import { ThemeContextProvider, ThemeActionsProvider } from './DynamicThemeContext';
import React, { Suspense, useEffect, useRef } from 'react';

import type { FlowThemeProgress } from './engine/types';
import { FlowThemeErrorBoundary } from './FlowThemeErrorBoundary';
import { resolveFlowTheme, type FlowThemeDefinition } from './flowThemeRegistry';
import { useFlowThemeTransitionBackdrop } from './FlowThemeTransition';
// Loaded with the layer rather than the lazy backdrop so the header tone and
// frosted cards still match the static fallback if the backdrop chunk fails.
import './flowThemes.css';

const loadBackdrop = () => import('./FlowThemeBackdrop');
const createLazyBackdrop = () => React.lazy(loadBackdrop);
// React.lazy caches a failed import for good, so a failure swaps in a fresh
// lazy component: the current flow keeps its static fallback and the next
// themed flow tries the download again.
let LazyFlowThemeBackdrop = createLazyBackdrop();
const handleBackdropError = () => {
  LazyFlowThemeBackdrop = createLazyBackdrop();
};

/**
 * Starts downloading a theme's code (the backdrop, scene and Lottie bursts).
 * Call it as soon as the flow's theme is known (while the flow is still
 * loading) so it is ready when the first question appears instead of popping
 * in after it. Resolves to true once the code is loaded; never rejects.
 */
export const preloadFlowTheme = (value?: string | null): Promise<boolean> => {
  const theme = resolveFlowTheme(value);
  return Promise.all([loadBackdrop(), theme.loadScene(), theme.loadBursts()])
    .then(() => true)
    .catch(() => false);
};

/** The theme's static swatch in the backdrop's place, used when the animated theme can't run. */
const FlowThemeStaticBackdrop: React.FC<{
  theme: FlowThemeDefinition;
  hidden: boolean;
  onReadyChange?: (ready: boolean) => void;
}> = ({ theme, hidden, onReadyChange }) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  useFlowThemeTransitionBackdrop(backdropRef, { swatch: theme.swatch, ready: true, hidden });
  const onReadyChangeRef = useRef(onReadyChange);
  onReadyChangeRef.current = onReadyChange;
  useEffect(() => {
    // The swatch is all this theme will show, so it is ready as soon as it mounts.
    onReadyChangeRef.current?.(true);
    return () => onReadyChangeRef.current?.(false);
  }, []);
  return (
    <div
      ref={backdropRef}
      className="flow-theme-static-backdrop pointer-events-none fixed inset-0 -z-10"
      aria-hidden="true"
      data-testid="flow-theme-backdrop-fallback"
      data-flow-theme={theme.id}
      data-flow-theme-hidden={hidden ? 'true' : undefined}
      style={{ background: theme.swatch }}
    />
  );
};

export interface FlowThemeLayerProps {
  /** The flow's `theme` value; unknown values fall back to the default mountain. */
  theme?: string | null;
  themeContext?: ThemeContext;
  /** Approved app action. Omit in isolated demos without a course destination. */
  onBackToCourse?: () => void;
  progress: FlowThemeProgress | null;
  /**
   * Keep the layer mounted but invisible and paused (e.g. while an embedded
   * roleplay step owns the page), so it resumes without replaying its entrance.
   */
  hidden?: boolean;
  /**
   * Whether the backdrop is showing (its first frame, or the static fallback).
   * Header chrome that depends on the theme should wait for this.
   */
  onReadyChange?: (ready: boolean) => void;
}

/** Renders a flow's animated theme backdrop behind the page. Theme code loads on demand. */
export const FlowThemeLayer: React.FC<FlowThemeLayerProps> = ({ theme, progress, themeContext, onBackToCourse, hidden = false, onReadyChange }) => {
  const definition = resolveFlowTheme(theme);
  const Overlay = definition.Overlay;
  if (!progress) {
    return null;
  }
  return (
    <FlowThemeErrorBoundary
      resetKey={definition.id}
      fallback={<FlowThemeStaticBackdrop theme={definition} hidden={hidden} onReadyChange={onReadyChange} />}
      onError={handleBackdropError}
    >
      <Suspense fallback={null}>
        <LazyFlowThemeBackdrop
          key={definition.id}
          theme={definition}
          progress={progress}
          themeContext={themeContext}
          hidden={hidden}
          onReadyChange={onReadyChange}
          handoff
          trackRenderMode
        />
      </Suspense>
      {Overlay && themeContext && !hidden ? (
        <FlowThemeErrorBoundary resetKey={definition.id} fallback={null}>
          <ThemeContextProvider value={themeContext}>
            <ThemeActionsProvider onBackToCourse={onBackToCourse}>
            <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" dir={themeContext.identity.direction} style={{ fontSize: `${themeContext.preferences.textScale}rem` }} data-testid="flow-theme-overlay">
              <Suspense fallback={null}><Overlay /></Suspense>
            </div>
            </ThemeActionsProvider>
          </ThemeContextProvider>
        </FlowThemeErrorBoundary>
      ) : null}
    </FlowThemeErrorBoundary>
  );
};
